import { Button, ButtonTypes, SimpleButton } from "./Button";
import { sanitize } from "isomorphic-dompurify";
import { isAvailable, LinkType, OptionsType } from "../../utils/baseTypes";
import { motion } from "framer-motion";
import { blur_opacity_variant } from "../../utils/transition";
import { useState } from "react";
import { ModalForm } from "../Form/ModalForm";

interface ContentProps {
  htype: "h2" | "h3";
  headline: string;
  subline?: string;
  halign?: string;
  hpos: "headline-over-text" | "headline-beside-text" | string;
  tpos: "has-text-left" | "has-text-centered" | string;
  text: string;
  ctapos?: "cta-under-headline" | "cta-under-text" | "cta-center" | string;
  number_cols?: string;
  link: LinkType[] | LinkType | false;
  disableText?: boolean;
  disableHeadline?: boolean;
  cta_type?: "link" | "berlinfahrten-form" | "contact-form";
  button_label?: string;
  options?: OptionsType;
}

export const Content = ({
  tpos,
  htype,
  headline,
  hpos,
  text = "",
  link,
  subline,
  halign,
  ctapos = "",
  number_cols = "",
  disableHeadline,
  cta_type,
  button_label,
  options,
}: ContentProps) => {
  let headlineJSX;
  switch (htype) {
    case "h2":
      headlineJSX = (
        <>
          <h2>
            <strong
              dangerouslySetInnerHTML={{
                __html: sanitize(headline),
              }}
            />
          </h2>
          {subline && (
            <span
              className="subline"
              dangerouslySetInnerHTML={{
                __html: sanitize(subline),
              }}
            />
          )}
        </>
      );
      break;
    case "h3":
      headlineJSX = (
        <>
          <h3>
            <strong
              dangerouslySetInnerHTML={{
                __html: sanitize(headline),
              }}
            />
          </h3>
          {subline && (
            <span
              className="subline"
              dangerouslySetInnerHTML={{
                __html: sanitize(subline),
              }}
            />
          )}
        </>
      );
      break;
    default:
      headlineJSX = (
        <>
          <h3>
            <strong
              dangerouslySetInnerHTML={{
                __html: sanitize(headline),
              }}
            />
          </h3>
          {subline && (
            <span
              className="subline"
              dangerouslySetInnerHTML={{
                __html: sanitize(subline),
              }}
            />
          )}
        </>
      );
      break;
  }

  const contentJSX = (
    <div
      className={`content ${number_cols}`}
      dangerouslySetInnerHTML={{
        __html: sanitize(text, {
          ADD_ATTR: ["target"],
        }),
      }}
    />
  );

  const Link = isAvailable<LinkType[] | LinkType>(link) ? link : false;

  let linksJSX;
  if (Link) {
    if (Array.isArray(Link)) {
      linksJSX = Link.map(
        (link, key) =>
          link.url &&
          link.title && (
            <Button
              key={key}
              className="my-1_8 mr-4 text-link"
              type="primary"
              target={link.target ? link.target : "_self"}
              title={link.title}
              href={link.url}
            >
              {link.title}
            </Button>
          )
      );
    } else {
      linksJSX = Link.url && Link.title && (
        <Button
          className="my-1_8 text-link"
          type="primary"
          target={Link.target ? Link.target : "_self"}
          title={Link.title}
          href={Link.url}
        >
          {Link.title}
        </Button>
      );
    }
  }
  const [modalOpen, setModalOpen] = useState(false);
  const form =
    cta_type === "berlinfahrten-form"
      ? "berlinfahrten"
      : cta_type === "contact-form"
      ? "contactform"
      : undefined;
  const ctaJSX =
    form === undefined ? (
      linksJSX
    ) : (
      <motion.div variants={blur_opacity_variant}>
        {button_label && (
          <SimpleButton
            text={button_label}
            className={`my-1_8 btn is-primary ${
              tpos === "has-text-centered" ? "mx-auto" : ""
            }`}
            onClick={() => setModalOpen(true)}
          />
        )}
      </motion.div>
    );

  let leftClass, rightClass;
  let leftJSX;

  switch (hpos) {
    case "headline-over-text":
      leftClass = "is-12 pb-0";
      rightClass = "is-12";
      leftJSX =
        number_cols === "two-columns" ? (
          <div className="columns">
            <div className="column is-12 is-6-desktop">{headlineJSX}</div>
          </div>
        ) : (
          headlineJSX
        );
      break;
    case "headline-beside-text":
      leftClass = "is-12 is-5-desktop";
      rightClass = "is-12 is-offset-1-desktop is-6-desktop";
      leftJSX = headlineJSX;
      break;
    default:
      leftClass = rightClass = "is-12";
      break;
  }

  return (
    <>
      <div className={`columns is-multiline text-content-wrapper`}>
        {!disableHeadline && (
          <div className={`column ${leftClass} ${halign ?? ""} ${tpos}`}>
            {leftJSX}
            {ctapos === "cta-under-headline" && ctaJSX}
          </div>
        )}
        <div className={`column ${rightClass} ${tpos}`}>
          {contentJSX}
          {ctapos === "cta-under-text" && ctaJSX}
        </div>
        {ctapos === "cta-center" && (
          <div className="column is-12">
            <div className="link-wrapper">{ctaJSX}</div>
          </div>
        )}
      </div>
      {form !== undefined && options && (
        <ModalForm
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          options={options}
          form={form}
        />
      )}
    </>
  );
};
